import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useGlobalEvent } from "../../common/hooks/useGlobalEvent";
import Condition from "../../components/Conditional/Condition";
import PopupDialog from "../../components/elements/PopupDialog/PopupDialog";
import { useCartContext } from "../../contexts/CartServiceContext/CartServiceContext";
import { useCatalogueContext } from "../../contexts/CatalogueContext/CatalogueContext";
import { useConfigContext } from "../../contexts/ConfigContext/ConfigContext";
import { APP_MODE, IStallMode } from "../../contexts/ConfigContext/types";
import { ViewOnlyModeAlert } from "../ProductListPage/ProductList/ViewOnlyModeAlert";
import { useViewOnlyModeAlert } from "../ProductListPage/ProductList/ViewOnlyModeAlert/useViewOnlyModeAlert";
import CartContainer from "./CartContainer/CartContainer";
import CartHeader from "./CartHeader/CartHeader";
import RemoveCartItemModal from "./CartModals/RemoveCartItemModal";
import "./CartPage.scss";
import "./CartPage_flint.scss";
import "./CartPage_kokoro.scss";
import "./CartPage_pdd.scss";
import "./CartPage_shabuDays.scss";
import "./CartPage_wang.scss";
import { CheckoutButton } from "./CheckoutButton/CheckoutButton";
import EmptyCart from "./EmptyCart/EmptyCart";
import { SendToKitchenButton } from "./SendToKitchenButton";
import { CART_PAGE_EVENT } from "./constants";
import useOosDetection from "./hooks/useOosDetection";

const CartPage = () => {
	const { t } = useTranslation();
	const { state: cart } = useCartContext((state) => state.cart);
	const { state: appMode } = useConfigContext((state) => state.appMode);
	const { state: stallMode } = useConfigContext((state) => state.stallMode);
	const { state: config } = useConfigContext((state) => state.config);
	const { state: catalogueProducts } = useCatalogueContext((state) => state.products);
	const isDynamicQrMode = config?.dynamic_qr === true && stallMode === IStallMode.TORIKIZOKU;
	const { hasOosItems, oosItemDetails, removeOosItems } = useOosDetection();
	const [showOosModal, setShowOosModal] = useState(false);
	const [showItemLimitModal, setShowItemLimitModal] = useState(false);

	const handleShowItemLimitModal = useCallback(() => {
		setShowItemLimitModal(true);
	}, []);

	useGlobalEvent(CART_PAGE_EVENT.SHOW_ITEM_LIMIT_MODAL, handleShowItemLimitModal);

	const viewModeAlert = useViewOnlyModeAlert({
		autoShow: appMode === APP_MODE.VIEW,
		persistOnClose: false,
	});
	const shouldShowViewModeAlert = useMemo(
		() => appMode === APP_MODE.VIEW && viewModeAlert.isVisible,
		[appMode, viewModeAlert.isVisible]
	);
	const shouldShowCheckoutButton = useMemo(() => {
		return appMode !== APP_MODE.VIEW && !!cart && cart.length > 0 && !isDynamicQrMode;
	}, [appMode, cart, isDynamicQrMode]);

	const shouldShowSendToKitchenButton = useMemo(() => {
		return isDynamicQrMode && !!cart && cart.length > 0;
	}, [isDynamicQrMode, cart]);

	useEffect(() => {
		if (hasOosItems && cart.length > 0 && catalogueProducts.length > 0 && !showOosModal) {
			setShowOosModal(true);
		}
	}, [hasOosItems, cart.length, catalogueProducts.length, showOosModal]);

	const handleRemoveOosItems = useCallback(() => {
		setShowOosModal(false);
		removeOosItems();
	}, [removeOosItems]);

	const handleCloseOosModal = useCallback(() => {
		setShowOosModal(false);
	}, []);

	const oosPopupMessage = useMemo(() => {
		if (oosItemDetails.length > 0) {
			return (
				<>
					<div className="OosMessage">{t("cartPage.oosMessage")}</div>
					<div className="OosItemsList">
						{oosItemDetails.map((itemDetail, index) => (
							<div key={index + itemDetail.title} className="OosItem">
								{itemDetail.title}
								<Condition
									check={!!itemDetail.unavailableItems && itemDetail.unavailableItems.length > 0}
								>
									<div className="OosSubItems">
										<div className="OosSubItemsLabel">{t("cartPage.oosUnavailableItems")}</div>
										{itemDetail.unavailableItems?.map((subItem, subIndex) => (
											<div key={subIndex + subItem} className="OosSubItem">
												{subItem}
											</div>
										))}
									</div>
								</Condition>
							</div>
						))}
					</div>
				</>
			);
		}

		return null;
	}, [oosItemDetails, t]);

	const oosPopupButtons = useMemo(
		() => [
			{
				text: t("cartPage.oosRemove"),
				onClick: handleRemoveOosItems,
				className: "PrimaryButton",
			},
		],
		[t, handleRemoveOosItems]
	);

	return (
		<div className="CartPage">
			<CartHeader />
			<Condition check={cart.length === 0}>
				<EmptyCart />
			</Condition>
			<Condition check={cart.length > 0}>
				<CartContainer cart={cart} />
			</Condition>
			<div className="CartPageFooters">
				<Condition check={shouldShowViewModeAlert}>
					<ViewOnlyModeAlert />
				</Condition>
				<Condition check={shouldShowCheckoutButton}>
					<CheckoutButton />
				</Condition>
				<Condition check={shouldShowSendToKitchenButton}>
					<SendToKitchenButton />
				</Condition>
			</div>
			<RemoveCartItemModal />
			<PopupDialog
				data-testid="cart-item-limit-dialog"
				className="ItemLimitDialog"
				open={showItemLimitModal}
				title={t("itemLimit.exceeded.title")}
				content={<span>{t("itemLimit.exceeded.message")}</span>}
				buttons={[
					{
						text: t("itemLimit.exceeded.confirm"),
						onClick: () => setShowItemLimitModal(false),
						className: "BtnItemLimitConfirm",
					},
				]}
				onClose={() => setShowItemLimitModal(false)}
			/>
			{oosItemDetails.length > 0 && (
				<PopupDialog
					data-testid="cart-oos-popup"
					open={showOosModal}
					title={t("cartPage.oosTitle")}
					onClose={handleCloseOosModal}
					content={<div className="ErrorContent">{oosPopupMessage}</div>}
					buttons={oosPopupButtons}
					className="CheckoutErrorPopup"
				/>
			)}
		</div>
	);
};

export default React.memo(CartPage);
